<script setup lang="ts">
import { customValidators } from "@/i18n/utils/i18n-validators";
import { ApiClientError, type ApiError } from "@shopware/api-client";
import { getShippingMethodDeliveryTime } from "@shopware/helpers";
import { useVuelidate } from "@vuelidate/core";
import type { operations } from "#shopware";
import { useTemplateRef, defineAsyncComponent } from "vue";
import { useGtmTracking } from '~/composables/useGtmTracking';

const PaypalCheckoutPayPalButton = defineAsyncComponent(() => import("~/components/paypal/CheckoutPayPalButton.vue"));
const PaypalCheckoutCreditCardButton = defineAsyncComponent(() => import("~/components/paypal/CheckoutCreditCardButton.vue"));
const PaypalCheckoutSepaButton = defineAsyncComponent(() => import("~/components/paypal/CheckoutSepaButton.vue"));


const { required, minLength, requiredIf, email } = customValidators();
const { resolveApiErrors, scrollToError } = useApiErrorsResolver("account_login");

defineOptions({
  name: "CheckoutPage",
});
definePageMeta({
  layout: "checkout",
});

const { push } = useRouter();
const { getCountries, getStatesForCountry } = useCountries();
const { getSalutations } = useSalutations();
const { pushInfo, pushError, pushSuccess } = useNotifications();
const { t } = useI18n();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { getPaymentMethods, getShippingMethods, createOrder, shippingMethods, paymentMethods } =
  useCheckout();
const { logout, isLoggedIn, isGuestSession, user, defaultBillingAddressId, defaultShippingAddressId, refreshUser } = useUser();
const { apiClient } = useShopwareContext();
const {
  refreshSessionContext,
  selectedShippingMethod: shippingMethod,
  selectedPaymentMethod: paymentMethod,
  setShippingMethod,
  setPaymentMethod,
  activeShippingAddress,
  setActiveShippingAddress,
  activeBillingAddress,
  setActiveBillingAddress,
} = useSessionContext();
const {
  cart,
  cartItems,
  subtotal,
  totalPrice,
  shippingCosts,
  shippingTotal,
  taxRate,
  isVirtualCart,
  refreshCart,
  clearCart,
} = useCart();
const { customerAddresses, loadCustomerAddresses, setDefaultCustomerShippingAddress, setDefaultCustomerBillingAddress } = useAddress();
const isLoading = reactive<{ [key: string]: boolean }>({});
const { trackBeginCheckout, trackAddShippingInfo, trackAddPaymentInfo } = useGtmTracking();
const editPersonalInfo = ref(false);

const isVoucherOpen = ref(false);
// const customerComment = ref<string>("test order for paypal cards, will remove after testing");
const customerComment = ref<string>("");

// Calculate total bundle discount from cart items directly
const totalCartBundleDiscount = computed(() => {
  return cartItems.value.reduce((total, item) => {
    const payload = item?.payload as any;
    const bundleData =
      payload?.selectedConfiguration?.selectedBundleVariantData || {};
    const itemDiscount = Object.values(bundleData).reduce((sum: number, bundle: any) => {
      return sum + (bundle?.discountAmount || 0);
    }, 0);
    return total + itemDiscount * (item.quantity || 1);
  }, 0);
});

const totalProductDiscount = computed((): number => {
  return cartItems.value
    .filter((item) => item.type === "product")
    .reduce((total, item) => {
      const list = item?.priceDefinition?.listPrice ?? 0;
      const unit = item?.priceDefinition?.price ?? 0;
      const diff = list > unit ? Math.round(list - unit) : 0;
      return total + diff * (item.quantity ?? 1);
    }, 0);
});

// Sum of promotion line items' totalPrice (typically negative = discount)
const totalPromotionAmount = computed(() => {
  return cartItems.value
    .filter((item) => item.type === "promotion")
    .reduce((sum, item) => sum + (item.price?.totalPrice ?? 0), 0);
});

const promotionSavings = computed(() => Math.abs(totalPromotionAmount.value));

// Handle comment changes from CheckoutShipping component
function handleCommentChanged(comment: string) {
  customerComment.value = comment;
}

// Calculate net price from gross price (gross prices are fixed)
const calculateNetFromGross = (grossPrice: number, taxRate: number) => {
  if (!grossPrice || !taxRate) return grossPrice || 0;
  // Net = Gross / (1 + VAT rate / 100)
  return grossPrice / (1 + taxRate / 100);
};

// Calculate VAT amount from gross price (gross prices are fixed)
const calculateVATFromGross = (grossPrice: number, taxRate: number) => {
  if (!grossPrice || !taxRate) return 0;
  // VAT = Gross - Net
  return grossPrice - calculateNetFromGross(grossPrice, taxRate);
};

watch([isLoggedIn, isGuestSession], async ([isLogged, isLoggedGuest]) => {
  if (isLogged || isLoggedGuest) {
    // When the user has just registered/logged in on the checkout,
    // make sure the session context, addresses, shipping and payment
    // methods are loaded – this mirrors what happens on a full reload.
    await refreshSessionContext();
    if (isLogged) {
      if (defaultShippingAddressId.value && activeShippingAddress.value?.id !== defaultShippingAddressId.value) {
        await setActiveShippingAddress({ id: defaultShippingAddressId.value });
      }
      if (defaultBillingAddressId.value && activeBillingAddress.value?.id !== defaultBillingAddressId.value) {
        await setActiveBillingAddress({ id: defaultBillingAddressId.value });
      }
      await refreshSessionContext();
    }
    await Promise.allSettled([
      loadCustomerAddresses(),
      !isVirtualCart.value ? getShippingMethods({ forceReload: true }) : null,
      getPaymentMethods({ forceReload: true }),
    ]);

    // Set current step to 2 when user logs in
    currentStep.value = 2;
  } else {
    // Reset to step 1 when user logs out
    currentStep.value = 1;
  }
});

/**
 * Broadcast channel logout refresh
 */
watch(isLoggedIn, async (newValue, oldValue) => {
  if (!newValue && oldValue) {
    location.reload();
  }
});

const selectedShippingMethod = computed({
  get(): string {
    return shippingMethod.value?.id || "";
  },
  async set(shippingMethodId: string) {
    isLoading[shippingMethodId] = true;
    await setShippingMethod({ id: shippingMethodId });
    await refreshPaymentMethod();
    // Refresh cart to update shipping costs and rule-based pseudo products
    await refreshCart();
    isLoading[shippingMethodId] = false;
  },
});

const selectedPaymentMethod = computed({
  get(): string {
    return paymentMethod.value?.id || "";
  },
  async set(paymentMethodId: string) {
    isLoading[paymentMethodId] = true;
    await setPaymentMethod({ id: paymentMethodId });
    await refreshShippingMethod();
    // Refresh cart so backend rule-based pseudo products update
    await refreshCart();
    isLoading[paymentMethodId] = false;
  },
});

const selectedShippingAddress = computed({
  get(): string {
    return activeShippingAddress.value?.id || "";
  },
  async set(shippingAddressId: string) {
    isLoading[`shipping-${shippingAddressId}`] = true;
    await setActiveShippingAddress({ id: shippingAddressId });
    if (isLoggedIn.value) {
      await setDefaultCustomerShippingAddress(shippingAddressId);
      await refreshSessionContext();
      await refreshUser();
    }
    await Promise.allSettled([
      !isVirtualCart.value ? refreshShippingMethod() : null,
      refreshPaymentMethod(),
    ]);
    // Refresh cart to update shipping costs after address change
    if (!isVirtualCart.value) {
      await refreshCart();
    }

    if (shippingAddressId === selectedBillingAddress.value)
      customShipping.value = false;
    isLoading[`shipping-${shippingAddressId}`] = false;
  },
});

const selectedBillingAddress = computed({
  get(): string {
    return activeBillingAddress.value?.id || "";
  },
  async set(billingAddressId: string) {
    isLoading[`billing-${billingAddressId}`] = true;
    await setActiveBillingAddress({ id: billingAddressId });
    if (isLoggedIn.value) {
      await setDefaultCustomerBillingAddress(billingAddressId);
      await refreshSessionContext();
      await refreshUser();
    }
    await Promise.allSettled([
      !isVirtualCart.value ? refreshShippingMethod() : null,
      refreshPaymentMethod(),
    ]);
    if (billingAddressId === selectedShippingAddress.value)
      customShipping.value = false;
    isLoading[`billing-${billingAddressId}`] = false;
  },
});

const isCartLoading = computed(() => {
  return !cart.value;
});

const isCheckoutAvailable = computed(() => {
  return cartItems.value.length > 0;
});

const isUserSession = computed(() => isLoggedIn.value || isGuestSession.value);

const customShipping = ref(true);

const terms = reactive({
  tos: false,
  revocation: false,
});

const termsBox = useTemplateRef("termsBox");
const shippingMethodBox = useTemplateRef("shippingMethodBox");

const placeOrder = async () => {
  placeOrderTriggered.value = true;

  // Check if user is logged in
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    const errorFields = document.querySelector("#checkout-billing-address");
    if (errorFields) {
      errorFields.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  // Check if cart has items
  if (!isCheckoutAvailable.value) {
    pushError(t("checkout.validation.cartEmpty"));
    return;
  }

  if (!termsSelected.value) {
    termsBox.value?.scrollIntoView();
    pushError(t("checkout.validation.termsRequired"));
    return;
  }
  if (!beforeCreateOrderValidation()) return;

  isLoading.placeOrder = true;

  try {
    // Store cart data temporarily before creating order
    const cartData = {
      items: cartItems.value.map(item => ({
        id: item.id,
        referencedId: item.referencedId || item.id,
        quantity: item.quantity,
        type: item.type,
        payload: item.payload
      })),
      customerComment: customerComment.value.trim()
    };

    // Store in sessionStorage for potential cart recreation
    if (process.client) {
      sessionStorage.setItem('tempCartData', JSON.stringify(cartData));
    }

    let order;

    // If there's a customer comment, use the API directly
    if (customerComment.value.trim()) {
      const response = await apiClient.invoke("createOrder post /checkout/order", {
        body: {
          customerComment: customerComment.value.trim()
        }
      });
      order = response.data;
    } else {
      // Use the default createOrder function if no comment
      order = await createOrder();
    }

    pushSuccess(t("checkout.messages.orderPlacedSuccess"));

    // For async payment gateways (PayPal, etc.): redirect to payment provider first,
    // then user returns to finish page after successful payment (Shopware standard flow).
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const finishUrl = `${origin}${formatLink(`/checkout/success/${order.id}/paid`)}`;
    const errorUrl = `${origin}${formatLink(`/checkout/success/${order.id}/unpaid`)}`;

    const paymentResponse = await apiClient.invoke(
      "handlePaymentMethod post /handle-payment",
      {
        body: {
          orderId: order.id,
          finishUrl,
          errorUrl,
        },
      },
    );

    const redirectUrl = paymentResponse?.data?.redirectUrl;

    if (redirectUrl && typeof redirectUrl === "string" && typeof window !== "undefined") {
      try {
        new URL(redirectUrl);
        if (process.client) {
          sessionStorage.setItem("checkout_order_placed", "1");
        }
        await clearCart();
        window.location.href = redirectUrl;
        return;
      } catch {
        // Invalid URL: fall through to success page
      }
    }

    // Synchronous payment (invoice, etc.): clear cart and go to success page
    if (process.client) {
      sessionStorage.setItem("checkout_order_placed", "1");
    }
    await clearCart();
    await push(formatLink(`/checkout/success/${order.id}`));
  } catch (error) {
    // if (error instanceof ApiClientError) {
      for (const errorItem of error.details.errors) {
        if (errorItem?.detail) {
          pushError(errorItem.detail);
        }
      }
    // } else {
    //   pushError(t("checkout.messages.orderPlacementError"));
    // }
  } finally {
    isLoading.placeOrder = false;
  }
};

const termsSelected = computed(() => {
  return terms.tos && (isVirtualCart.value ? terms.revocation : true);
});
const placeOrderTriggered = ref(false);

onMounted(async () => {
  await refreshSessionContext();

  if (isLoggedIn.value) {
    if (defaultShippingAddressId.value && activeShippingAddress.value?.id !== defaultShippingAddressId.value) {
      await setActiveShippingAddress({ id: defaultShippingAddressId.value });
    }
    if (defaultBillingAddressId.value && activeBillingAddress.value?.id !== defaultBillingAddressId.value) {
      await setActiveBillingAddress({ id: defaultBillingAddressId.value });
    }
    await refreshSessionContext();
  }

  // Set initial step based on user session
  if (isUserSession.value) {
    currentStep.value = 2;
  }

  isLoading.shippingMethods = true;
  isLoading.paymentMethods = true;

  await Promise.allSettled([
    isUserSession.value ? loadCustomerAddresses() : null,
    !isVirtualCart.value ? getShippingMethods() : null,
    getPaymentMethods(),
  ]);

  isLoading.shippingMethods = false;
  isLoading.paymentMethods = false;

  if (cart.value) {
    trackBeginCheckout(cart.value);
  }
});

const loginModalController = useModal();
const addAddressModalController = useModal();
const billingAddressSelectionModalController = useModal();
const shippingAddressSelectionModalController = useModal();
const lastOpenSelectionModal = ref<'billing' | 'shipping' | null>(null);

const handleAddressSaved = async (addressId?: string): Promise<void> => {
  if (addressId){
    if (lastOpenSelectionModal.value === "billing") {
      selectedBillingAddress.value = addressId;
    } else if (lastOpenSelectionModal.value === "shipping") {
      selectedShippingAddress.value = addressId;
    }
  }

  await refreshAddresses();
};

const refreshAddresses = async () => {
  isLoading.addresses = true;
  const previousShippingCountry = activeShippingAddress.value?.countryId;
  
  await refreshSessionContext();
  await loadCustomerAddresses();
  
  // Check if shipping address country changed and refresh shipping methods
  const currentShippingCountry = activeShippingAddress.value?.countryId;
  if (previousShippingCountry && currentShippingCountry && previousShippingCountry !== currentShippingCountry) {
    if (!isVirtualCart.value) {
      await refreshShippingMethod();
    }
  }
  
  addAddressModalController.close();
  isLoading.addresses = false;
};

async function invokeLogout() {
  try {
    await logout();
  } finally {
    await push(formatLink("/"));
  }
}

const refreshShippingMethod = async () => {
  isLoading.shippingMethods = true;
  const currentShippingMethodId = shippingMethod.value?.id;
  
  await getShippingMethods({ forceReload: true });
  isLoading.shippingMethods = false;
  
  // After refreshing shipping methods, check if current selection is still valid
  // Check directly if the current shipping method ID exists in the new list
  const currentMethodExists = currentShippingMethodId && shippingMethods.value.some(
    (method) => method.id === currentShippingMethodId
  );
  
  if (!currentMethodExists && shippingMethods.value.length > 0) {
    // Current shipping method is no longer available, select first available
    await setShippingMethod({ id: shippingMethods.value[0].id });
  }
  
  // Refresh cart to update shipping costs
  if (!isVirtualCart.value) {
    await refreshCart();
  }
};

const refreshPaymentMethod = async () => {
  isLoading.paymentMethods = true;
  await getPaymentMethods({ forceReload: true });
  isLoading.paymentMethods = false;
};

const shippingExists = computed(() => {
  return shippingMethods.value.find(
    (method) => method.id === selectedShippingMethod.value
  );
});

const paymentExists = computed(() => {
  return paymentMethods.value?.find(
    (method) => method.id === selectedPaymentMethod.value
  );
});

// Step 2 (shipping & payment) validation for "Continue to Check & Order" button
const canProceedToReviewStep = computed(() => {
  // Require a selected payment method that still exists in the list
  if (!paymentMethod.value || !paymentExists.value) {
    return false;
  }

  // For non-virtual carts, also require a valid shipping method
  if (
    !isVirtualCart.value &&
    (!selectedShippingMethod.value || shippingExists.value === undefined)
  ) {
    return false;
  }

  return true;
});

const beforeCreateOrderValidation = () => {
  // Check for valid addresses
  if (!activeShippingAddress.value) {
    pushError(t("checkout.validation.addressRequired"));
    return false;
  }

  if (!activeBillingAddress.value) {
    pushError(t("checkout.validation.addressRequired"));
    return false;
  }

  // Check for payment method and that it still exists in the list
  if (!paymentMethod.value || !paymentExists.value) {
    pushError(t("checkout.validation.paymentMethodRequired"));
    return false;
  }

  if (!isVirtualCart.value) {
    if (!selectedShippingMethod.value || shippingExists.value === undefined) {
      shippingMethodBox.value?.scrollIntoView();
      pushError(t("checkout.validation.shippingMethodRequired"));
      return false;
    }
  }

  return true;
};

/** True when all validations from placeOrder (223–242) + beforeCreateOrderValidation pass – used to enable PayPal/credit card buttons */
const canPlaceOrderPayPal = computed(() => {
  if (!isUserSession.value || !isCheckoutAvailable.value || !termsSelected.value) {
    return false;
  }
  if (
    !activeShippingAddress.value ||
    !activeBillingAddress.value ||
    !paymentMethod.value ||
    !paymentExists.value
  ) {
    return false;
  }
  if (
    !isVirtualCart.value &&
    (!selectedShippingMethod.value || shippingExists.value === undefined)
  ) {
    return false;
  }
  return true;
});

/** Credit card / redirect payment methods (PayPal Plus card+SEPA, etc.) - show express credit card button */

const { updatePersonalInfo } = useUser();

const handleChangeBaseInfo = async (data: {
  firstName?: string;
  lastName?: string;
  salutationId?: string;
}) => {
  try {
    await updatePersonalInfo({
      firstName: data.firstName ?? "",
      lastName: data.lastName ?? "",
      salutationId: data.salutationId ?? "",
    });
  } catch (error) {
    // if (error instanceof ApiClientError)
      for (const errorItem of error.details.errors) {
        if (errorItem?.detail) {
          pushError(errorItem.detail);
        }
      }
  }

  await refreshSessionContext();
  editPersonalInfo.value = false;
};



const currentStep = ref(1);

watch(currentStep, (step) => {
  if (step !== 2 || !cart.value) return;
  if (!isVirtualCart.value && shippingMethod.value) {
    trackAddShippingInfo(
      cart.value,
      shippingMethod.value.translated?.name ?? shippingMethod.value.name ?? "",
    );
  }
  if (paymentMethod.value) {
    trackAddPaymentInfo(
      cart.value,
      paymentMethod.value.translated?.name ?? paymentMethod.value.name ?? "",
    );
  }
});

const changePaymentMethod = async (methodId?: string) => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  await refreshPaymentMethod();
  const resolvedId = methodId ?? paymentMethod.value?.id;
  const method = paymentMethods.value?.find((m) => m.id === resolvedId);
  if (cart.value && method) {
    trackAddPaymentInfo(cart.value, method.translated?.name ?? method.name ?? "");
  }
};

const changeShippingMethod = async (methodId?: string) => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  await refreshShippingMethod();
  const resolvedId = methodId ?? shippingMethod.value?.id;
  const method = shippingMethods.value?.find((m) => m.id === resolvedId);
  if (cart.value && method) {
    trackAddShippingInfo(cart.value, method.translated?.name ?? method.name ?? "");
  }
};

// Watch for user session changes and show appropriate notifications
watch(isUserSession, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    location.reload();
  }
});

// Watch for cart changes and show notifications if needed (e.g. items removed).
// Do not show "cart empty" when we just cleared the cart after placing an order (PayPal or regular).
watch(isCheckoutAvailable, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (typeof window !== 'undefined' && sessionStorage.getItem('checkout_order_placed')) {
      sessionStorage.removeItem('checkout_order_placed');
      return;
    }
    // pushError(t("checkout.validation.cartEmpty"));
  }
});

// Watch for shipping address country changes to refresh shipping methods
watch(
  () => activeShippingAddress.value?.countryId,
  async (newCountryId, oldCountryId) => {
    if (newCountryId && oldCountryId && newCountryId !== oldCountryId && !isVirtualCart.value) {
      await refreshShippingMethod();
    }
  }
);

// Watch for shipping methods changes to auto-select first if current is invalid
watch(
  () => shippingMethods.value?.map(m => m.id).join(','),
  async (newMethodIds, oldMethodIds) => {
    // Only proceed if methods actually changed and we have methods available
    if (newMethodIds && newMethodIds !== oldMethodIds && !isVirtualCart.value) {
      const currentShippingMethodId = shippingMethod.value?.id;
      if (currentShippingMethodId && shippingMethods.value && shippingMethods.value.length > 0) {
        // Check if current shipping method exists in the new list
        const currentMethodExists = shippingMethods.value.some(
          (method) => method.id === currentShippingMethodId
        );
        
        if (!currentMethodExists) {
          // Current shipping method is no longer available, select first available
          await setShippingMethod({ id: shippingMethods.value[0].id });
        }
      } else if (!currentShippingMethodId && shippingMethods.value && shippingMethods.value.length > 0) {
        // No shipping method selected, select first available
        await setShippingMethod({ id: shippingMethods.value[0].id });
      }
    }
  }
);

const addNewBillingAddress = () => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  addAddressModalController.open();
};

const addNewShippingAddress = () => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  addAddressModalController.open();
};

const openBillingAddressSelection = () => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  lastOpenSelectionModal.value = 'billing';
  billingAddressSelectionModalController.open();
};

const openShippingAddressSelection = () => {
  if (!isUserSession.value) {
    pushError(t("checkout.validation.userSessionRequired"));
    return;
  }
  lastOpenSelectionModal.value = 'shipping';
  shippingAddressSelectionModalController.open();
};

const handleBillingAddressSelect = (addressId: string) => {
  selectedBillingAddress.value = addressId;
  billingAddressSelectionModalController.close();
};

const handleShippingAddressSelect = (addressId: string) => {
  selectedShippingAddress.value = addressId;
  shippingAddressSelectionModalController.close();
};

const handleAddNewFromSelection = (addressType: 'billing' | 'shipping') => {
  // Close the selection modal first
  if (addressType === 'billing') {
    billingAddressSelectionModalController.close();
  } else {
    shippingAddressSelectionModalController.close();
  }
  
  // Use nextTick to ensure the first modal is fully closed before opening the second
  nextTick(() => {
    addAddressModalController.open();
  });
};

const handleSwitchFromAddToSelection = () => {
  // Close the add address modal first
  addAddressModalController.close();
  
  // Use nextTick to ensure the first modal is fully closed before opening the second
  nextTick(() => {
    // Reopen the appropriate selection modal based on context
    if (lastOpenSelectionModal.value === 'billing') {
      billingAddressSelectionModalController.open();
    } else if (lastOpenSelectionModal.value === 'shipping') {
      shippingAddressSelectionModalController.open();
    } else {
      // Fallback to billing if no context
      billingAddressSelectionModalController.open();
    }
  });
};

const newsletter = ref(false);
const {
  isNewsletterSubscriber,
  newsletterUnsubscribe,
  newsletterSubscribe,
  getNewsletterStatus,
  confirmationNeeded,
} = useNewsletter();

const newsletterErrors = ref<ApiError[]>([]);

const syncNewsletterStatus = async () => {
  if (!isLoggedIn.value) return;

  try {
    await getNewsletterStatus();
    newsletter.value = isNewsletterSubscriber.value;
  } catch {
    // Guest or non-subscriber: keep local checkbox state
  }
};

const updateNewsletterStatus = async () => {
  newsletterErrors.value = [];
  try {
    if (!newsletter.value) {
      await newsletterSubscribe({
        email: user.value?.email || "",
        option: "subscribe",
      });
      pushSuccess(t("newsletter.messages.newsletterSubscribed"));
    } else {
      await newsletterUnsubscribe(user.value?.email || "");
      pushSuccess(t("newsletter.messages.newsletterUnsubscribed"));
    }
  } catch (error) {
    // if (error instanceof ApiClientError) {
        newsletterErrors.value = resolveApiErrors(error.details.errors);
        for (const error of newsletterErrors.value) {
          pushError(error);
        }
      // }
  } finally {
    await syncNewsletterStatus();
  }
};

onBeforeMount(async () => {
  await syncNewsletterStatus();
});

const goToNextStep = (step: number) => {
  // When moving from step 2 (shipping & payment) to step 3 (review),
  // ensure both payment and (for non-virtual carts) shipping are selected
  // and still present in the current method lists.
  if (currentStep.value === 2 && step === 3) {
    if (!paymentMethod.value || !paymentExists.value) {
      pushError(t("checkout.validation.paymentMethodRequired"));
      return;
    }

    if (
      !isVirtualCart.value &&
      (!selectedShippingMethod.value || shippingExists.value === undefined)
    ) {
      shippingMethodBox.value?.scrollIntoView();
      pushError(t("checkout.validation.shippingMethodRequired"));
      return;
    }
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
  currentStep.value = step;
};

const { 
        isPayPalCheckout, 
    } = usePayPalSession();
const paypalMethod = computed(() => {
    return paymentMethods.value?.find(
        (method) => method.shortName === "pay_pal_payment_handler",
    );
});
const isPayPalSelected = computed(() => {
    if (!paypalMethod.value) return false;
    // Check if PayPal is selected (either express checkout or regular PayPal selection)
    return selectedPaymentMethod.value === paypalMethod.value.id || isPayPalCheckout.value;
});

const creditCardMethod = computed(() => {
    return paymentMethods.value?.find(
        (method) => method.shortName === "a_c_d_c_handler",
    );
});

const isCreditCardSelected = computed(() => {
    if (!creditCardMethod.value) return false;
    return selectedPaymentMethod.value === creditCardMethod.value.id;
});

const sepaMethod = computed(() => {
    return paymentMethods.value?.find(
        (method) => method.shortName === "s_e_p_a_handler",
    );
});

const isSepaSelected = computed(() => {
    if (!sepaMethod.value) return false;
    return selectedPaymentMethod.value === sepaMethod.value.id;
});

// Add PayPal payment handlers
const handlePayPalPaymentComplete = async (orderId: string | undefined) => {
  try {
    // Verify the order exists and is accessible
    if (!orderId || typeof orderId !== 'string' || orderId.trim() === '') {
      throw new Error("No order ID received from PayPal payment");
    }

    // Cart is already cleared in the PayPal button component before payment processing
    // No need to refresh cart here as it's already empty
    
    // Navigate to success page
    await push(formatLink(`/checkout/success/${orderId}`));
    
  } catch (error) {
    console.error("[Checkout] Error handling PayPal payment completion:", error);
    pushError("Something went wrong with order confirmation. Please contact support.");
    
    // Only redirect if we have a valid order ID
    if (orderId && typeof orderId === 'string' && orderId.trim() !== '') {
      await push(formatLink(`/checkout/success/${orderId}`));
    }
  }
};

const handlePayPalPaymentError = (error: string) => {
  console.error("[Checkout] PayPal payment error:", error);
  pushError(error);
};

// Show a full-screen loading overlay while the redirect is in progress
// so the brief 'empty cart' state isn't visible to the user.
const paymentRedirecting = ref(false);
const handlePaymentRedirecting = () => {
  paymentRedirecting.value = true;
};
</script>

<template>
  <div class="checkout-wrapper mt-12 md:mt-16">
    <!-- Full-screen loading overlay: shown after payment succeeds while cart clears + page redirects -->
    <Transition name="fade">
      <div
        v-if="paymentRedirecting"
        class="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm"
        aria-live="polite"
        aria-label="Processing your order"
      >
        <div class="flex flex-col items-center gap-4">
          <svg class="animate-spin h-12 w-12 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p class="text-lg font-semibold text-gray-700">{{ $t('checkout.paypal.processingOrder') }}</p>
        </div>
      </div>
    </Transition>
    <div class="container small">
      <CheckoutSteps
        v-if="isCheckoutAvailable || isCartLoading"
        :current-step="currentStep"
      />

      <div class="checkout-content mt-12 md:mt-16">
        <template v-if="isCheckoutAvailable || isCartLoading">
          
          <template v-if="currentStep === 1">
            <div
              v-if="!isUserSession"
              class="flex flex-col xl:flex-row gap-6 lg:gap-16"
              :class="{
                'opacity-20': isCartLoading,
              }"
            >
              <div
                v-if="!isUserSession"
                class="flex flex-col w-full gap-2"
              >
                <div class="flex flex-col flex-1 items-start">
                  <div class="mt-0 w-full">
                    <div>
                      <CheckoutRegistration>
                        <template #sign-in-link>
                          <a
                            href="#"
                            class="whitespace-nowrap font-medium text-primary hover:text-dark"
                            data-testid="checkout-sign-in-link"
                            @click="loginModalController.open"
                          >
                            {{ $t("checkout.signIn") }}
                          </a>
                        </template>
                      </CheckoutRegistration>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-if="!isUserSession"
                class="login-wrapper flex flex-col gap-5 w-full xl:w-auto xl:sticky xl:top-10 xl:h-fit pt-12 md:pt-0 border-t border-gray-200 md:border-t-0 mt-8 md:mt-0"
              >
                <div class="w-full h-fit xl:w-[420px]">
                  <div class="mt-0">
                    <div>
                      <CheckoutLoginForm class="!px-0 !py-0" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-if="currentStep === 2">
            <div class="payments-delivery-wrapper">
              <div class="small">
                <CheckoutShipping
                  @shipping-method-changed="changeShippingMethod"
                />
                <CheckoutPayments
                  @payment-method-changed="changePaymentMethod"
                />
                <div class="btn-wrapper mt-10">
                  <button
                    aria-label="Continue to Check &amp; Order"
                    class="btn btn-primary text-nowrap"
                    :disabled="!canProceedToReviewStep"
                    @click="goToNextStep(3)"
                  >
                    {{ $t("checkout.continueToCheckAndOrder") }}
                  </button>
                </div>
              </div>
            </div>
          </template>
          <template v-if="currentStep === 3">
            <h1
              v-if="isUserSession"
              class="heading text-2xl mb-7 md:mb-10 lg:text-6xl 3xl:text-8xl font-marcellus text-grey-700 md:leading-none leading-[1.2]"
            >
              {{ $t("checkout.completeOrder") }}
            </h1>
            <div
              class="flex flex-col xl:flex-row gap-6 lg:gap-16"
              :class="{
                'opacity-20': isCartLoading,
              }"
            >
              <div class="flex flex-col w-full gap-2">
                <!-- Personal Information Section commmented for now -->
                <template v-if="isUserSession">
                  <div
                    class="order-section-wrapper flex flex-col md:flex-row gap-0 md:gap-16 space-y-8 md:space-y-0"
                  >
                    <div class="flex flex-col flex-1 items-start">
                      <h3
                        class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1 mb-3"
                      >
                        {{ $t("checkout.billingAddressLabel") }}
                      </h3>
                      <hr class="w-full border-solid" />
                      <CheckoutAddressCard
                        class="address-card mt-6 mb-6"
                        :address="activeBillingAddress"
                        :countries="getCountries"
                        :salutations="getSalutations"
                        :can-set-default="false"
                        @success="refreshAddresses()"
                      />
                      <div class="mt-auto mb-0 pt-2 flex justify-center md:justify-start">
                        <button
                          aria-label="Change billing address"
                          class="btn btn-secondary text-nowrap"
                          @click="openBillingAddressSelection"
                        >
                          {{ $t("checkout.changeBillingAddress") }}
                        </button>
                      </div>


                    </div>
                    <!-- Payment Section -->
                    <div class="flex flex-col flex-1 items-start">
                      <h3
                        class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1 mb-3"
                      >
                        {{ $t("checkout.shippingAddressLabel") }}
                      </h3>
                      <hr class="w-full border-solid" />
                      <CheckoutAddressCard
                        class="mt-7"
                        :address="activeShippingAddress"
                        :countries="getCountries"
                        :salutations="getSalutations"
                        :can-set-default="false"
                        @success="refreshAddresses()"
                      />
                      <!-- Continue Button -->
                      <div class="mt-6 pt-2 flex justify-center md:justify-start">
                        <button
                          aria-label="Change shipping address"
                          class="btn btn-secondary text-nowrap"
                          @click="openShippingAddressSelection"
                        >
                          {{ $t("checkout.changeShippingAddress") }}
                        </button>
                      </div>

                    </div>
                  </div>

                  <!-- Shipping Method Section -->
                  <div
                    v-if="!isVirtualCart"
                    class="shipping-payment-wrapper space-y-12 mt-4"
                  >
                    <div class="mt-6">
                      <h3
                        class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1 mb-3"
                      >
                        {{ $t("checkout.shippingMethodLabel") }}
                      </h3>
                      <hr class="w-full border-solid" />
                      <div class="mt-7">
                        <div
                          v-if="isLoading['shippingMethods']"
                          class="w-60 h-24"
                        >
                          <div
                            class="flex animate-pulse flex-row items-top pt-4 h-full space-x-5"
                          >
                            <div
                              class="w-4 bg-secondary-300 h-4 mt-1 rounded-full"
                            />
                            <div class="flex flex-col space-y-3">
                              <div class="w-36 bg-secondary-300 h-6 rounded-md" />
                              <div class="w-24 bg-secondary-300 h-6 rounded-md" />
                            </div>
                          </div>
                        </div>
                        <div v-else class="space-y-5">
                          <label
                            class="flex items-start gap-3 cursor-pointer select-none"
                          >
                            <input
                              :id="`shipping-method-${shippingMethod.id}`"
                              :value="shippingMethod.id"
                              name="shipping-method-selected"
                              type="radio"
                              class="mt-1"
                              checked
                              :data-testid="`checkout-shipping-method-${shippingMethod.id}`"
                            />
                            <div class="space-y-2">
                              <p class="font-semibold">
                                {{ shippingMethod.translated.name }}
                                <span
                                  v-if="
                                    getShippingMethodDeliveryTime(shippingMethod)
                                  "
                                >
                                  ({{
                                    getShippingMethodDeliveryTime(shippingMethod)
                                  }})
                                </span>
                                <NuxtLink
                                  aria-label="zur Lieferkostenübersicht"
                                  :to="$t('checkout.deliveryCostOverviewLink')"
                                  class="text-primary font-semibold underline ml-1"
                                  >{{ $t("checkout.deliveryCostOverview") }}</NuxtLink
                                >
                              </p>
                              <span
                                v-if="shippingMethod.translated.description"
                                class="text-sm text-grey-700 mt-1 block"
                              >
                                {{ shippingMethod.translated.description }}
                              </span>
                              <div v-if="shippingMethod.media?.url">
                                <img
                                  loading="lazy"
                                  :src="shippingMethod.media.url"
                                  alt="payment-image"
                                />
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    <!-- Payment Method Section -->
                    <div class="mt-6">
                      <h3
                        class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1 mb-3"
                      >
                        {{ $t("checkout.paymentMethodLabel") }}
                      </h3>
                      <hr class="w-full border-solid" />
                      <div class="space-y-5 mt-7">
                        <div v-if="isLoading['paymentMethods']" class="w-60 h-24">
                          <div
                            class="flex animate-pulse flex-row items-top pt-4 h-full space-x-5"
                          >
                            <div
                              class="w-4 bg-secondary-300 h-4 mt-1 rounded-full"
                            />
                            <div class="flex flex-col space-y-3">
                              <div class="w-36 bg-secondary-300 h-6 rounded-md" />
                              <div class="w-24 bg-secondary-300 h-6 rounded-md" />
                            </div>
                          </div>
                        </div>

                        <div v-else class="space-y-5">
                          <label
                            class="flex items-start gap-3 cursor-pointer select-none"
                          >
                            <input
                              :id="`payment-method-${paymentMethod.id}`"
                              v-model="selectedPaymentMethod"
                              :value="paymentMethod.id"
                              name="payment-method-selected"
                              type="radio"
                              class="mt-1"
                              checked
                              :data-testid="`checkout-payment-method-${paymentMethod.id}`"
                            />
                            <div class="space-y-4">
                              <p>
                                <span class="font-semibold">
                                  {{ paymentMethod.translated.name }}
                                </span>
                                <span
                                  v-if="paymentMethod.translated.description"
                                  class="text-sm text-grey-700 mt-1 block"
                                >
                                  {{ paymentMethod.translated.description }}
                                </span>
                              </p>
                              <div v-if="paymentMethod.media?.url">
                                <img
                                  loading="lazy"
                                  :src="paymentMethod.media.url"
                                  :alt="`Logo of ${paymentMethod.shortName}`"
                                />
                              </div>
                            </div>
                          </label>
                          <div class="text-sm text-grey-700 mt-2 block">
                            <b>{{ $t("checkout.paymentInfoTitle") }}</b> {{ $t("checkout.paymentInfo") }}
                          </div>
                          <div class="mt-8 flex md:justify-start">
                            <button
                              aria-label="Änderung Zahlungen & Lieferung"
                              class="btn btn-secondary text-nowrap"
                              @click="goToNextStep(2)"
                            >
                              {{ $t("checkout.changePaymentShipping") }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div class="mt-12 md:mt-16">
                  <div class="grid gap-4 bg-white">
                    <ul role="list" class="-my-4 pl-0 list-none">
                      <li
                        v-for="cartItem in cartItems"
                        :key="cartItem.id"
                        class="flex py-3"
                      >
                        <CheckoutCartItem
                          :cart-item="cartItem"
                        />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Cart Summary -->
              <section
                class="cart-summary-wrapper flex flex-col gap-5 w-full xl:w-auto xl:sticky xl:top-10 tall:top-[150px] xl:h-fit"
              >
                <div
                  class="w-full h-fit xl:w-[420px] p-4 md:p-6 rounded-[8px] md:rounded-[15px] bg-white border border-gray-300"
                >
                  <div class="cart-summary-content flex flex-col">
                    <h3 class="text-lg font-normal text-primary mb-4">
                      {{ $t("cart.orderSummary") }}
                    </h3>
                    <!-- Summary content will go here -->
                    <div class="space-y-2">
                      <!-- Base amount -->
                      <div class="flex justify-between items-center">
                        <span class="text-gray-950">{{ $t("cart.summe") }}</span>
                        <SharedPrice
                          :value="subtotal + promotionSavings + totalCartBundleDiscount + totalProductDiscount"
                          class="font-normal"
                          data-testid="cart-subtotal"
                        />
                      </div>

                      <!-- Shipping costs -->
                      <div class="flex flex-col">
                        <div class="flex justify-between items-center">
                          <div
                            class="flex items-center gap-1 font-bold text-primary"
                          >
                            <span class=" ">{{ $t("cart.shippingCosts") }}</span>
                              <SharedLandingPageLink
                                :open-in-dialog="true"
                                class="text-sm text-bold cursor-help"
                              >
                                ⓘ
                              </SharedLandingPageLink>
                          </div>

                          <SharedPrice
                            :value="shippingTotal ?? 0"
                            class="font-normal"
                            data-testid="cart-shipping-cost"
                          />
                        </div>
                      </div>

                      <!-- Promotion / voucher total (PromotionItem line items) -->
                      <div
                        v-if="promotionSavings > 0"
                        class="flex justify-between items-center"
                      >
                        <span class="text-gray-950">{{
                          $t("cart.couponDiscount")
                        }}</span>
                        <SharedPrice
                          :value="promotionSavings"
                          class="font-medium text-red"
                        >
                          <template #beforePrice><span>-</span></template>
                        </SharedPrice>
                      </div>
                      <!-- Product list-price & bundle discounts -->
                      <div
                        v-if="totalCartBundleDiscount > 0 || totalProductDiscount > 0"
                        class="flex justify-between items-center"
                      >
                        <span class="text-gray-950">{{
                          $t("cart.bundleDiscount")
                        }}</span>
                        <SharedPrice
                          :value="totalCartBundleDiscount + totalProductDiscount"
                          class="font-medium text-red"
                          data-testid="cart-bundle-discount"
                        >
                          <template #beforePrice><span>-</span></template>
                        </SharedPrice>
                      </div>

                      <!-- Total -->
                      <div
                        class="flex justify-between items-center font-bold pb-2"
                      >
                        <span class="font-bold text-gray-950">{{
                          $t("cart.orderTotal")
                        }}</span>
                        <SharedPrice
                          :value="totalPrice"
                          data-testid="cart-subtotal"
                          class="text-2xl text-red"
                        />
                      </div>

                      <!-- VAT information -->
                      <div
                        class="flex justify-between items-center text-sm text-gray-950 pt-3 border-t border-gray-200"
                      >
                        <span>{{ $t("cart.net") }}</span>
                        <SharedPrice
                          :value="calculateNetFromGross(subtotal, taxRate) + calculateNetFromGross(shippingTotal ?? 0, taxRate)"
                          class="text-secondary-900"
                          data-testid="cart-gesamtbestellwert"
                        />
                      </div>
                      <div
                        class="flex justify-between items-center text-sm text-gray-950"
                      >
                        <span>{{ $t("cart.vat", { taxRate: taxRate }) }}</span>
                        <SharedPrice
                          :value="calculateVATFromGross(subtotal, taxRate) + calculateVATFromGross(shippingTotal ?? 0, taxRate)"
                          class="text-secondary-900"
                          data-testid="cart-gesamtbestellwert"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="w-full h-fit xl:w-[420px] p-4 md:p-6 rounded-[8px] md:rounded-[15px] bg-white border border-gray-300"
                >
                  <div class="cart-summary-content flex flex-col">
                    <h3 class="text-lg font-normal mb-4 text-gray-900">
                      {{ $t("checkout.termsLabelHeading") }}
                    </h3>
                    <p class="font-semibold mb-3">
                      {{ $t("checkout.termsLabelText") }}
                      <a href="#" class="text-primary font-semibold underline">{{
                        $t("checkout.termsLabelLink")
                      }}</a>
                    </p>
                    <div class="space-y-3">
                      <label
                        class="flex items-start gap-3 cursor-pointer select-none"
                        :class="{
                          'text-red': !termsSelected && placeOrderTriggered,
                        }"
                        for="tos"
                      >
                        <input
                          id="tos"
                          v-model="terms.tos"
                          :value="terms.tos"
                          name="tos"
                          type="checkbox"
                          class="mt-1 checkbox-input"
                          data-testid="checkout-t&c-checkbox-tos"
                        />
                        <p class="">
                          {{ $t("checkout.termsLabel") }}
                        </p>
                      </label>
                      <label
                        v-if="isVirtualCart"
                        for="revocation"
                        class="flex items-start gap-3 cursor-pointer select-none"
                        :class="{
                          'text-red': !termsSelected && placeOrderTriggered,
                        }"
                      >
                        <input
                          id="revocation"
                          v-model="terms.revocation"
                          :value="terms.revocation"
                          name="revocation"
                          type="checkbox"
                          class="mt-1 checkbox-input"
                          data-testid="checkout-t&c-checkbox-revocation"
                        />
                        <p class="">
                          {{ $t("checkout.digitalTerms") }}
                        </p>
                      </label>
                      <label
                        class="flex items-start gap-3 cursor-pointer select-none"
                      >
                        <input
                          id="newsletter-checkbox"
                          v-model="newsletter"
                          name="newsletter-checkbox"
                          type="checkbox"
                          class="mt-1 checkbox-input"
                          @click="updateNewsletterStatus"
                        />
                        <p class="">
                          {{ $t("checkout.termsLabel2") }}
                        </p>
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  class="flex gap-5 md:gap-10 flex-col md:flex-row xl:flex-col w-full 2xl:w-auto justify-end 2xl:justify-normal"
                >
                  <!-- Voucher code collapsible section -->
                  <div class="">
                    <!-- kommentar -->
                    <div class="w-full h-fit md:w-[420px] p-4 md:p-6 rounded-[8px] md:rounded-[15px] bg-white border border-gray-300">
                      <textarea v-model="customerComment" name="comment" id="comment" class="w-full min-h-[120px] rounded-md focus:outline-none focus:ring-primary focus:z-10 sm:text-sm placeholder-secondary-500 text-secondary-900 focus:border-primary resize-none" :placeholder="$t('checkout.customerCommentLabel')"></textarea>
                    </div>
                  </div>
                </div>
                <div
                  class="mt-5 flex gap-5 md:gap-10 flex-col md:flex-row xl:flex-col w-full 2xl:w-auto justify-end 2xl:justify-normal"
                >
                  
                  <!-- PayPal express checkout button -->
                  <PaypalCheckoutPayPalButton
                      v-if="isPayPalSelected"
                      :disabled="!canPlaceOrderPayPal"
                      :customer-comment="customerComment"
                      :is-user-session="!!isUserSession"
                      :is-checkout-available="isCheckoutAvailable"
                      @payment-complete="handlePayPalPaymentComplete"
                      @payment-error="handlePayPalPaymentError"
                      @payment-redirecting="handlePaymentRedirecting"
                      @place-order-triggered="() => placeOrderTriggered = true"
                      @place-order="placeOrder"
                      :terms-selected="termsSelected"
                      :place-order-triggered="placeOrderTriggered"
                      :shipping-method-selected="!!selectedShippingMethod"
                      :is-virtual-cart="isVirtualCart"
                      :payment-method-selected="!!selectedPaymentMethod"
                      :selected-payment-method-id="selectedPaymentMethod"
                    />

                  <!-- Express credit card checkout (PayPal CardFields iframes) -->
                  <PaypalCheckoutCreditCardButton
                    v-else-if="isCreditCardSelected"
                    :disabled="!canPlaceOrderPayPal"
                    :is-loading="!!isLoading['placeOrder']"
                    :customer-comment="customerComment"
                    :terms-selected="termsSelected"
                    :place-order-triggered="placeOrderTriggered"
                    :shipping-method-selected="!!selectedShippingMethod"
                    :is-virtual-cart="isVirtualCart"
                    :payment-method-selected="!!selectedPaymentMethod"
                    :selected-payment-method-id="selectedPaymentMethod"
                    :is-user-session="!!isUserSession"
                    :is-checkout-available="isCheckoutAvailable"
                    @place-order-triggered="() => placeOrderTriggered = true"
                    @place-order="placeOrder"
                    @payment-complete="handlePayPalPaymentComplete"
                    @payment-error="handlePayPalPaymentError"
                    @payment-redirecting="handlePaymentRedirecting"
                    @scroll-to-validation="(el) => { termsBox?.scrollIntoView(); if (el === 'shipping') shippingMethodBox?.scrollIntoView(); if (el === 'payment' && typeof document !== 'undefined') document.querySelector('#checkout-billing-address')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }"
                  />

                  <!-- SEPA Lastschrift via PayPal -->
                  <PaypalCheckoutSepaButton
                    v-else-if="isSepaSelected"
                    :disabled="!canPlaceOrderPayPal"
                    :is-loading="!!isLoading['placeOrder']"
                    :customer-comment="customerComment"
                    :terms-selected="termsSelected"
                    :place-order-triggered="placeOrderTriggered"
                    :shipping-method-selected="!!selectedShippingMethod"
                    :is-virtual-cart="isVirtualCart"
                    :payment-method-selected="!!selectedPaymentMethod"
                    :selected-payment-method-id="selectedPaymentMethod"
                    :is-user-session="!!isUserSession"
                    :is-checkout-available="isCheckoutAvailable"
                    @payment-complete="handlePayPalPaymentComplete"
                    @payment-error="handlePayPalPaymentError"
                    @payment-redirecting="handlePaymentRedirecting"
                  />

                  <!-- Regular place order button -->
                  <button
                    v-else
                    type="button"
                    :class="{
                      'cursor-not-allowed  animate-pulse': isLoading['placeOrder'],
                    }"
                    class="btn btn-primary 2xl:btn-full justify-center min-w-[120px] text-nowrap"
                    data-testid="checkout-place-order-button"
                    @click="placeOrder"
                  >
                    {{ $t("checkout.placeOrder") }}
                  </button>
                </div>
              </section>
            </div>
          </template>
        </template>
        <!-- Empty cart design -->
        <div v-else class="container small">
          <div
            class="flex flex-col items-center justify-center py-6 px-4 min-h-[500px]"
            data-testid="checkout-empty"
          >
            <div
              class="w-24 h-24 md:w-48 md:h-28 mb-0 md:mb-4 text-grey-400 flex items-center justify-center text-[60px] md:text-[100px]"
            >
              <i class="icon-bag-outline" aria-hidden="true"></i>
            </div>
            <h1 class="text-2xl md:text-3xl font-marcellus text-grey-700 mb-4">
              {{ $t("cart.emptyCartLabel") }}
            </h1>
            <p class="text-base md:text-lg text-grey-500 text-center mb-8">
              {{ $t("cart.emptyCartDescription") }}
            </p>
            <NuxtLink
              :to="formatLink(`/`)"
              class="inline-flex items-center px-6 py-3 border border-grey-500 rounded-full text-base font-medium text-grey-700 hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-300"
              data-testid="checkout-empty-continue-link"
            >
              {{ $t("checkout.goToHomepage") }}
              <i class="icon-arrow-right ml-2 text-lg" aria-hidden="true"></i>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <SharedModal :controller="loginModalController">
      <AccountLoginForm
        @close="loginModalController.close"
        @success="loginModalController.close"
      />
    </SharedModal>
    <SharedModal :controller="addAddressModalController">
      <SharedAccountAddressForm 
        @success="(addressId?: string) => handleAddressSaved(addressId ?? '')" 
        @switch-to-selection="() => handleSwitchFromAddToSelection()"
      />
    </SharedModal>
    
    <!-- Billing Address Selection Modal -->
    <SharedModal :controller="billingAddressSelectionModalController">
      <SharedAddressSelectionModal
        :addresses="customerAddresses"
        :current-address="activeBillingAddress"
        address-type="billing"
        :countries="getCountries"
        :salutations="getSalutations"
        @select="handleBillingAddressSelect"
        @add-new="() => handleAddNewFromSelection('billing')"
        @close="billingAddressSelectionModalController.close"
      />
    </SharedModal>
    
    <!-- Shipping Address Selection Modal -->
    <SharedModal :controller="shippingAddressSelectionModalController">
      <SharedAddressSelectionModal
        :addresses="customerAddresses"
        :current-address="activeShippingAddress"
        address-type="shipping"
        :countries="getCountries"
        :salutations="getSalutations"
        @select="handleShippingAddressSelect"
        @add-new="() => handleAddNewFromSelection('shipping')"
        @close="shippingAddressSelectionModalController.close"
      />
    </SharedModal>
  </div>
</template>
